import { Component } from '@theme/component';
import { firePendingSubmissionIfAny, stashPendingSubmission } from './oc-inquiry-measurement-v2.js';

/** sessionStorage/Klaviyo key for this form family. */
const FORM_KEY = 'custom-builds';

/**
 * Copy shown above the project-details textarea, keyed by the selected project type.
 * Mirrors the "Prompt" rows in the approved Forms/Form behavior spec.
 * @type {Record<string, string>}
 */
const DETAILS_PROMPTS = {
  backyard: 'What would you like to skate in your backyard? A short description is enough.',
  public: 'What would you like to create for your community? A short description is enough.',
  business: 'What would you like to create for your business? A short description is enough.',
  unsure: 'Tell us what you have in mind. It is fine if you are still exploring.',
};

const DEFAULT_DETAILS_PROMPT = 'Tell us about your project';

/** Project types that reveal the organization field. */
const ORGANIZATION_VISIBLE_FOR = new Set(['public', 'business']);

/**
 * @typedef {object} CustomBuildsFormRefs
 * @property {HTMLSelectElement} [projectTypeInput]
 * @property {HTMLElement} [detailsLabelText]
 * @property {HTMLTextAreaElement} [detailsInput]
 * @property {HTMLSelectElement} [stateInput]
 * @property {HTMLInputElement} [firstNameInput]
 * @property {HTMLInputElement} [emailInput]
 * @property {HTMLInputElement} [phoneInput]
 * @property {HTMLInputElement} [cityInput]
 * @property {HTMLElement} [organizationField]
 * @property {HTMLInputElement} [organizationInput]
 * @property {HTMLSelectElement} [budgetInput]
 * @property {HTMLSelectElement} [timingInput]
 * @property {HTMLInputElement} [marketingInput]
 * @property {HTMLTextAreaElement} [bodyInput]
 * @property {HTMLButtonElement} [submitButton]
 *
 * @extends {Component<CustomBuildsFormRefs>}
 */
class CustomBuildsFormComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    const preselect = this.dataset.preselect;
    const { projectTypeInput } = this.refs;

    if (preselect && projectTypeInput) {
      const option = projectTypeInput.querySelector(`option[data-key="${preselect}"]`);

      if (option instanceof HTMLOptionElement) {
        projectTypeInput.value = option.value;
      }
    }

    this.#syncProjectTypeBranch();

    // `.quote-form__success` only renders when Liquid's `form.posted_successfully?`
    // is true, so its presence is a reliable success signal after the page reload.
    firePendingSubmissionIfAny(FORM_KEY, Boolean(this.querySelector('.quote-form__success')));
  }

  /** Runs when the project type select changes. */
  handleProjectTypeChange() {
    this.#syncProjectTypeBranch();
  }

  /**
   * Composes the hidden `contact[body]` field from the individual inputs right
   * before the native form submits, since Shopify's contact endpoint only
   * delivers `name`, `email`, `phone` and `body`. Also stashes the submission
   * for the Klaviyo data push, picked up after the page reloads on success.
   */
  handleSubmit() {
    const { bodyInput, submitButton } = this.refs;

    if (bodyInput) {
      bodyInput.value = this.#composeBody();
    }

    this.#stashKlaviyoSubmission();

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
    }
  }

  /** Shows/hides the organization field and updates the details prompt for the current project type. */
  #syncProjectTypeBranch() {
    const { projectTypeInput, detailsLabelText, organizationField, organizationInput } = this.refs;
    const selectedOption = projectTypeInput?.selectedOptions[0];
    const key = selectedOption?.dataset.key ?? '';

    if (detailsLabelText) {
      detailsLabelText.textContent = DETAILS_PROMPTS[key] ?? DEFAULT_DETAILS_PROMPT;
    }

    const showOrganization = ORGANIZATION_VISIBLE_FOR.has(key);

    if (organizationField) organizationField.hidden = !showOrganization;
    if (organizationInput instanceof HTMLInputElement) organizationInput.disabled = !showOrganization;
  }

  #stashKlaviyoSubmission() {
    const {
      projectTypeInput,
      detailsInput,
      stateInput,
      cityInput,
      organizationInput,
      budgetInput,
      timingInput,
      marketingInput,
      firstNameInput,
      emailInput,
      phoneInput,
    } = this.refs;

    stashPendingSubmission(FORM_KEY, {
      email: emailInput?.value,
      firstName: firstNameInput?.value,
      phone: phoneInput?.value,
      sourceLabel: this.dataset.sourceLabel,
      family: 'Custom Builds',
      properties: {
        'Project type': projectTypeInput?.value,
        'Project details': detailsInput?.value,
        'Project state': stateInput?.value,
        'Project city': cityInput?.value,
        Organization:
          organizationInput instanceof HTMLInputElement && !organizationInput.disabled ? organizationInput.value : undefined,
        'Working budget': budgetInput?.value,
        'Target timing': timingInput?.value,
        'Marketing consent': Boolean(marketingInput?.checked),
      },
    });
  }

  /**
   * Builds a labeled, human-readable summary of every field that Shopify's
   * native contact form doesn't deliver on its own.
   * @returns {string}
   */
  #composeBody() {
    const { projectTypeInput, detailsInput, stateInput, cityInput, organizationInput, budgetInput, timingInput, marketingInput } =
      this.refs;

    /** @type {[string, string | undefined][]} */
    const rows = [
      ['Source', this.dataset.sourceLabel],
      ['Project type', projectTypeInput?.value],
      ['Project details', detailsInput?.value],
      ['Project state', stateInput?.value],
      ['Project city', cityInput?.value],
    ];

    if (organizationInput instanceof HTMLInputElement && !organizationInput.disabled && organizationInput.value) {
      rows.push(['Organization', organizationInput.value]);
    }

    rows.push(
      ['Working budget', budgetInput?.value],
      ['Target timing', timingInput?.value],
      ['Marketing consent', marketingInput?.checked ? 'Yes' : 'No']
    );

    return rows
      .filter(([, value]) => value)
      .map(([label, value]) => `${label}: ${value}`)
      .join('\n');
  }
}

if (!customElements.get('custom-builds-form-component')) {
  customElements.define('custom-builds-form-component', CustomBuildsFormComponent);
}
